import { cn } from '@/lib/utils'
import localeText from '@/locales/datagrid'
import { CircularProgress } from '@mui/material'
import {
    Cell,
    getCoreRowModel,
    getExpandedRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    Row,
    useReactTable,
} from '@tanstack/react-table'
import React, { useCallback } from 'react'
import { match } from 'ts-pattern'
import { useOptionalHook } from '../../hooks/useOptionalHook'
import { useTableApiContext } from '../../utils/table-context'
import { ColumnDefPro, GridColDefPro, ValidRowModel } from '../../utils/types'
import CellRenderer from './cell'
import { HeaderRenderer } from './cell-header'
import { translateColDef } from './integration-layer'

export type ApiRef = ReturnType<
    typeof React.useRef<ReturnType<typeof useReactTable<ValidRowModel>> | null>
>

interface IMainTableProps {
    apiRef: ApiRef
}

export default function MainTable({ apiRef }: IMainTableProps) {
    const {
        columns,
        rows,
        pagination,
        isLoading,
        sortModel = [],
        onSortModelChange,
        expanded,
        onExpandedChange,
        enableRowExpanding,
        totalRows,
    } = useTableApiContext()

    const isServerSide = totalRows !== undefined

    const translatedColumns = translateColDef(columns)
    const [sorting, setSorting] = useOptionalHook(sortModel, onSortModelChange)

    const table = useReactTable({
        data: rows,
        columns: translatedColumns,
        getCoreRowModel: getCoreRowModel(),
        ...(isServerSide
            ? { manualPagination: true, rowCount: totalRows }
            : { getPaginationRowModel: getPaginationRowModel() }),
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: enableRowExpanding
            ? getExpandedRowModel()
            : undefined,
        getSubRows: enableRowExpanding ? (row: any) => row.children : undefined,
        enableExpanding: enableRowExpanding,
        state: {
            pagination,
            sorting,
            ...(enableRowExpanding && { expanded }),
        },
        onSortingChange: setSorting,
        ...(enableRowExpanding && {
            onExpandedChange,
            filterFromLeafRows: true,
            maxLeafRowFilterDepth: 1,
            paginateExpandedRows: false,
        }),
    })

    const tableRef = React.useRef<HTMLTableElement>(null)

    React.useEffect(() => {
        if (apiRef) apiRef.current = table
    }, [table])

    // Scroll para o topo quando a página mudar
    React.useEffect(() => {
        const scrollContainer = tableRef.current?.parentElement
        if (scrollContainer) {
            scrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }, [pagination.pageIndex])

    return [
        <div
            key="loading"
            className={cn(
                'size-full flex-col items-center justify-center',
                isLoading ? 'flex' : 'hidden',
            )}
        >
            <CircularProgress size={40} />
        </div>,
        <table
            ref={tableRef}
            key="not-loading"
            className={cn(
                'w-full caption-bottom text-sm border-collapse',
                isLoading ? 'hidden' : '',
            )}
        >
            <thead className="sticky top-0 z-2 bg-card">
                <Headers table={table} />
            </thead>
            <tbody className="divide-y min-h-full *:last:border-b">
                <Rows table={table} />
            </tbody>
        </table>,
    ]
}

function Headers({
    table,
}: {
    table: ReturnType<typeof useReactTable<ValidRowModel>>
}) {
    const { density, heightControls } = useTableApiContext()

    return table.getHeaderGroups().map(headerGroup => (
        <tr
            key={headerGroup.id}
            className={cn(
                'data-[state=selected]:bg-muted/50 transition-colors select-none',
                'overflow-y-hidden *:px-2',
                match(density)
                    .with('compact', () => 'text-base/10 [&_th]:*:max-h-10')
                    .with('comfortable', () => 'text-base/15 [&_th]:*:max-h-15')
                    .otherwise(() => 'text-base/13 [&_th]:*:max-h-13'),
            )}
        >
            {headerGroup.headers.map((header, index, arr) => (
                <th
                    colSpan={header.colSpan}
                    title={
                        (header.column.columnDef as ColumnDefPro)._ogDef
                            .description
                    }
                    key={header.id}
                    className={cn(
                        'relative align-middle font-medium flex-nowrap group',
                        (header.column.columnDef as ColumnDefPro)._ogDef
                            .renderHeader
                            ? 'overflow-hidden text-nowrap'
                            : 'truncate',
                    )}
                >
                    <span
                        className={cn(
                            'flex items-center',
                            match(
                                (header.column.columnDef as ColumnDefPro)._ogDef
                                    .headerAlign,
                            )
                                .with('left', () => 'text-left justify-start')
                                .with(
                                    'center',
                                    () => 'text-center justify-center',
                                )
                                .with('right', () => 'text-right justify-end')
                                .exhaustive(),
                        )}
                        style={(() => {
                            if (heightControls.headerHeight != undefined)
                                return {
                                    height: heightControls.headerHeight + 'px',
                                    maxHeight:
                                        heightControls.headerHeight + 'px',
                                }
                        })()}
                    >
                        <HeaderRenderer header={header} />
                        {index !== arr.length - 1 && (
                            <span className="absolute h-3/5 w-[1px] border-l bg-transparent right-0 top-1/5" />
                        )}
                        <span className="absolute h-[1px] w-full border-b bg-transparent right-0 bottom-0" />
                    </span>
                </th>
            ))}
        </tr>
    ))
}

const DESTAQUE_CLASSES =
    'data-[state=selected]:bg-primary/15 hover:bg-primary/15 transition-colors duration-150'

function Rows({
    table,
}: {
    table: ReturnType<typeof useReactTable<ValidRowModel>>
}) {
    const rowsLen = useCallback(() => {
        return table.getRowModel().rows.length
    }, [table])
    // getRowModel já expande as rows
    if (rowsLen() > 0)
        return table
            .getRowModel()
            .rows.map(row => <RowCell key={row.original.id} row={row} />)

    return (
        <tr className={DESTAQUE_CLASSES}>
            <td
                colSpan={table.getAllFlatColumns().length}
                className="h-24 text-center p-2 align-middle"
            >
                {localeText.noRowsLabel}
            </td>
        </tr>
    )
}

function RowCell({ row }: { row: Row<ValidRowModel> }) {
    const {
        density,
        onRowClick,
        heightControls,
        getRowClassName,
        selectionModel,
    } = useTableApiContext()

    return (
        <tr
            data-id={row.original.id}
            data-state={selectionModel.includes(row.original.id) && 'selected'}
            className={cn(
                DESTAQUE_CLASSES,
                'whitespace-nowrap overflow-y-hidden *:px-2 text-base',
                getRowClassName?.({
                    id: row.original.id,
                    row: row.original,
                }),
            )}
        >
            {
                row.getVisibleCells().reduce(
                    (acc, cell) => {
                        const ogDef = (cell.column.columnDef as ColumnDefPro)
                            ._ogDef
                        const colSpan = (() => {
                            const fn = ogDef.colSpan
                            if (typeof fn === 'function') {
                                return fn({
                                    id: cell.id,
                                    value: cell.getValue(),
                                    row: cell.row.original,
                                })
                            } else if (typeof fn === 'number') {
                                return fn
                            }
                            return 1
                        })()

                        // Skip rendering cells that overlap with the previous colSpan
                        if (acc.remainingColSpan > 0) {
                            acc.remainingColSpan -= 1
                            return acc
                        }

                        acc.remainingColSpan = colSpan > 1 ? colSpan - 1 : 0

                        acc.cells.push(
                            <td
                                data-field={ogDef.field}
                                onClick={() => {
                                    onRowClick?.({
                                        id: cell.row.original.id,
                                        row: cell.row.original,
                                    })
                                }}
                                colSpan={colSpan}
                                key={cell.id}
                                className={cn(
                                    'align-middle',
                                    ogDef._hasCustomRenderCell
                                        ? 'overflow-hidden text-nowrap'
                                        : '',
                                    match(ogDef.align)
                                        .with(
                                            'left',
                                            () => 'text-left justify-start',
                                        )
                                        .with(
                                            'center',
                                            () => 'text-center justify-center',
                                        )
                                        .with(
                                            'right',
                                            () => 'text-right justify-end',
                                        )
                                        .exhaustive(),
                                    ogDef.cellClassName,
                                )}
                                style={getCellWidth(ogDef)}
                                title={getCellTitle(ogDef, cell)}
                            >
                                <span
                                    className={cn(
                                        'm-0 p-0 w-full flex flex-row items-center',
                                        'text-base', // fonte fixa
                                        match(density)
                                            .with(
                                                'compact',
                                                () => 'h-11 max-h-11 min-h-11',
                                            )
                                            .with(
                                                'comfortable',
                                                () => 'h-14 max-h-14 min-h-14',
                                            )
                                            .otherwise(
                                                () => 'h-13 max-h-13 min-h-13',
                                            ),
                                    )}
                                    style={getCellStyles(
                                        cell,
                                        heightControls,
                                        density,
                                        ogDef.align,
                                    )}
                                >
                                    <CellRenderer cell={cell} />
                                </span>
                            </td>,
                        )

                        return acc
                    },
                    {
                        cells: [] as React.ReactNode[],
                        remainingColSpan: 0,
                    },
                ).cells
            }
        </tr>
    )
}

function getCellWidth(ogDef: ColumnDefPro['_ogDef']): React.CSSProperties {
    const style = {} as React.CSSProperties

    const width = ogDef.width,
        minWidth = ogDef.minWidth,
        maxWidth = ogDef.maxWidth,
        flex = ogDef.flex

    if (minWidth != undefined) style.minWidth = minWidth
    if (maxWidth != undefined) style.maxWidth = maxWidth

    if (flex != undefined) {
        style.flex = flex
        return style
    }

    if (width != undefined) {
        style.width = width
        style.maxWidth = width
        style.minWidth ??= width
    }
    return style
}

function getCellStyles(
    cell: Cell<ValidRowModel, unknown>,
    heightControls: any,
    density: string,
    align: Required<GridColDefPro>['align'],
): React.CSSProperties {
    const styles = {} as React.CSSProperties

    switch (align) {
        case 'left':
            styles.justifyContent = 'flex-start'
            break
        case 'center':
            styles.justifyContent = 'center'
            break
        case 'right':
            styles.justifyContent = 'flex-end'
            break
        default:
            styles.justifyContent = 'normal'
            break
    }

    if (heightControls.getRowHeight) {
        const h =
            heightControls.getRowHeight({
                id: cell.row.id,
                row: cell.row.original,
                rowIndex: cell.row.index,
                density,
            }) + 'px'
        styles.height = h
        styles.maxHeight = h
        return styles
    } else if (heightControls.rowHeight != undefined) {
        styles.height = heightControls.rowHeight + 'px'
        styles.maxHeight = heightControls.rowHeight + 'px'
        return styles
    }

    return styles
}

function getCellTitle(
    ogDef: ColumnDefPro['_ogDef'],
    cell: Cell<ValidRowModel, unknown>,
): string | undefined {
    if (ogDef._hasCustomRenderCell) return undefined
    const value = cell.getValue()
    if (['string', 'number', 'date'].includes(typeof value))
        return String(value)
    return undefined
}
